import { useEffect, useRef, useState } from 'react';

import {
  PhotoUploadCancelled,
  removeVenuePhoto,
  uploadVenuePhoto,
  VenuePhotoRequestError,
} from '../../../../../api/venue-photo';
import type { VenueDetail } from '../../../../../api/venues';

import { cropAreaFor, isBelowMinimumSize, type CropPan } from './photo-crop';
import type { usePhotoFeedback } from './use-photo-feedback';
import type { PhotoSelection } from './use-photo-selection';

export type PhotoStep = 'idle' | 'cropping' | 'uploading' | 'confirming' | 'removing';

export interface VenueCacheActions {
  /** Shows the venue the server sent back. */
  apply: (venue: VenueDetail) => void;
  /** Fetches the venue again, for when the server's reply could not be read. */
  refresh: () => void;
}

export interface PhotoTransferInput {
  venueId: string;
  cache: VenueCacheActions;
  feedback: ReturnType<typeof usePhotoFeedback>;
  selection: PhotoSelection | null;
  zoom: number;
  pan: CropPan;
  setStep: (step: PhotoStep) => void;
  /** Called after a change went through. */
  onDone: () => void;
}

const GENERIC_MESSAGE = 'Something went wrong with the photo. Try again.';

/** Sending the framed photo, or removing the uploaded one, and what the person is told about it. */
export function usePhotoTransfer(input: PhotoTransferInput) {
  const { venueId, cache, feedback, selection, zoom, pan, setStep, onDone } = input;
  const [progress, setProgress] = useState(0);
  const running = useRef<AbortController | null>(null);

  useEffect(() => () => running.current?.abort(), []);

  const deliver = (venue: VenueDetail | null) => (venue ? cache.apply(venue) : cache.refresh());

  const save = async () => {
    if (!selection || isBelowMinimumSize(selection.size)) return;
    const { file, size } = selection;
    const controller = new AbortController();
    running.current = controller;
    feedback.dismissProblem();
    setProgress(0);
    setStep('uploading');
    feedback.announce('Uploading photo…');
    try {
      const crop = cropAreaFor(size, zoom, pan);
      deliver(await uploadVenuePhoto(venueId, file, crop, setProgress, controller.signal));
      feedback.announce('Photo saved');
      onDone();
    } catch (error) {
      setStep('cropping');
      if (error instanceof PhotoUploadCancelled) return;
      const aboutFile = error instanceof VenuePhotoRequestError && error.aboutFile;
      const message = error instanceof VenuePhotoRequestError ? error.message : GENERIC_MESSAGE;
      feedback.report(message, aboutFile ? file : null);
    }
  };

  const remove = async () => {
    feedback.dismissProblem();
    feedback.announce('');
    setStep('removing');
    try {
      const venue = await removeVenuePhoto(venueId);
      deliver(venue);
      const reverted = venue?.bookingPhoto.source === 'imported';
      feedback.announce(reverted ? 'Reverted to the imported photo' : 'Photo removed');
      onDone();
    } catch (error) {
      setStep('idle');
      feedback.report(
        error instanceof VenuePhotoRequestError ? error.message : GENERIC_MESSAGE,
        null,
      );
    }
  };

  return { progress, save, remove, cancelUpload: () => running.current?.abort() };
}
